import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router";
import { Button } from "../../../components/button/button";
import { Input } from "../../../components/input/input";
import { Spring } from "../../../components/spring/spring";
import { Text } from "../../../components/text/text";
import AuthAPI from "../../../api/AuthAPI";
import UserAPI from "../../../api/UserAPI";
import { setStatus } from "../../../features/statusReducer";
import { useAnimatedNavigate } from "../../../hooks/useAnimatedNavigate";
import styles from "../_styles/authPage.module.css";

const mapApiError = (error, fallback) => {
    return error?.response?.data?.detail || fallback;
};

export const LoginOtpPage = () => {
    const dispatch = useDispatch();
    const navigate = useAnimatedNavigate();
    const [searchParams] = useSearchParams();

    const initialChallengeId = useMemo(() => {
        return searchParams.get("challengeId") || "";
    }, [searchParams]);

    const inviteToken = useMemo(() => {
        return searchParams.get("inviteToken") || null;
    }, [searchParams]);

    const expiresIn = useMemo(() => {
        const rawValue = searchParams.get("expiresIn");
        const parsed = Number(rawValue);
        if (!rawValue || Number.isNaN(parsed) || parsed <= 0) {
            return null;
        }
        return parsed;
    }, [searchParams]);

    const [challengeId, setChallengeId] = useState(initialChallengeId);
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [errorText, setErrorText] = useState("");
    const [infoText, setInfoText] = useState("");

    useEffect(() => {
        setChallengeId(initialChallengeId);
    }, [initialChallengeId]);

    useEffect(() => {
        if (!initialChallengeId) {
            setErrorText("Сессия подтверждения не найдена. Выполните вход еще раз.");
        }
    }, [initialChallengeId]);

    useEffect(() => {
        setErrorText("");
    }, [code]);

    const redirectByStatus = async () => {
        const status = await UserAPI.fetchUserStatus();
        dispatch(setStatus(status));

        if (status.isSuperAdmin) {
            navigate("/superadmin");
            return;
        }

        if (status.isUser) {
            navigate("/");
            return;
        }

        if (status.isAdmin) {
            navigate("/admin");
            return;
        }

        navigate("/auth/register");
    };

    const openLogin = () => {
        if (inviteToken) {
            navigate(`/auth/login?inviteToken=${inviteToken}`);
            return;
        }
        navigate("/auth/login");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!challengeId) {
            setErrorText("Сессия подтверждения устарела. Повторите вход.");
            return;
        }

        const normalizedCode = code.trim();
        if (normalizedCode.length < 4) {
            setErrorText("Введите корректный код из письма");
            return;
        }

        setLoading(true);
        setInfoText("");

        try {
            await AuthAPI.verifyLoginOtp(challengeId, normalizedCode);

            if (inviteToken) {
                try {
                    await AuthAPI.acceptInvite(inviteToken);
                } catch (error) {
                    const detail = error?.response?.data?.detail;
                    if (error?.response?.status !== 409) {
                        setErrorText(detail || "Не удалось привязать аккаунт к кофейне. Обратитесь к администратору.");
                        return;
                    }
                }
            }

            await redirectByStatus();
        } catch (error) {
            setErrorText(mapApiError(error, "Не удалось подтвердить код"));
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!challengeId) {
            setErrorText("Невозможно отправить код повторно. Выполните вход еще раз.");
            return;
        }

        setResendLoading(true);
        setErrorText("");

        try {
            const response = await AuthAPI.resendLoginOtp(challengeId);
            if (response.challengeId) {
                setChallengeId(response.challengeId);
            }
            setInfoText("Новый код отправлен на вашу почту");
        } catch (error) {
            setErrorText(mapApiError(error, "Не удалось отправить код повторно"));
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <>
            <Text as="div" variant="h3" className={styles.title} size={28} weight={700}>
                Подтверждение входа
            </Text>

            <form onSubmit={handleSubmit} className={styles.form}>
                <Input
                    value={code}
                    setValue={setCode}
                    label="Код из письма"
                    placeholder="123456"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                />

                {
                    expiresIn &&
                    <Text as="div" variant="caption" size={14}>
                        Код действует около {Math.max(1, Math.ceil(expiresIn / 60))} мин.
                    </Text>
                }

                <Spring />

                <Button
                    buttonType="submit"
                    text={loading ? "Подтверждаем..." : "Подтвердить код"}
                    disabled={loading || resendLoading}
                />

                <Button
                    type="secondary"
                    text={resendLoading ? "Отправляем..." : "Отправить код повторно"}
                    onClick={handleResend}
                    disabled={loading || resendLoading}
                />

                <Button
                    type="secondary"
                    text="Вернуться к входу"
                    onClick={openLogin}
                    disabled={loading || resendLoading}
                />

                {
                    !!infoText &&
                    <Text as="div" align="center" variant="body" size={14} lineHeight="1.3">
                        {infoText}
                    </Text>
                }

                {
                    !!errorText &&
                    <Text as="div" align="center" variant="body" className={styles.errorText} size={14} lineHeight="1.3">
                        {errorText}
                    </Text>
                }
            </form>
        </>
    );
};
